/**
 * Ticket Lifecycle v2 — FOH Timer Handoff (Batch 3)
 *
 * Pure, shop-level advisor analytics for the manager/owner/org-admin
 * Analytics page. Where the per-advisor `computeAdvisorMetrics`
 * (Passport, slice 4E) tells the story of ONE person, this helper tells
 * the story of the SHOP: who is accepting, how fast, what the FOH queue
 * looks like right now, and — using the Batch 1/2 structured handoff
 * metadata — how "tech-blocking" pause-kicks compare to "closeout"
 * stop-kicks.
 *
 * Source of truth, reused (no drift with Passport):
 *   - `computeAdvisorMetrics` produces every per-advisor headline number
 *     (accepted / kicked-back / accept lag mean/p50/p95 / FOH custody).
 *     We call it once per advisor so the Analytics table and the
 *     Passport card always agree.
 *   - `mean` / `percentile` (advisorMetrics) for shop-level + trigger
 *     segmentation.
 *   - `readCustodyHandoffMetadata` / `isPauseKickReason` / `isStopKickReason`
 *     / `isManualKickReason` / `getCustodyHandoffReasonLabel` /
 *     `IMPLICIT_TAKEOVER_REASON` (custodyHandoffReasons) for trigger +
 *     reason classification.
 *   - `getPendingAcceptAgingLevel` / `CUSTODY_AGING_THRESHOLDS_MS`
 *     (custodyAging) for the FOH bottleneck view.
 *
 * Pure: no I/O, no clock reads. The caller supplies the event slices,
 * the current custody snapshot, an advisor display-name map, and an
 * explicit `referenceTime` for aging math (keeps it deterministic).
 *
 * Boundary: imports only custody/advisor lifecycle modules. No
 * scheduling / timer / parking / rollover / bay / invariants code.
 *
 * Scope note: shop-only. This helper deliberately does NOT roll up
 * district / region / org — that is out of Batch 3 scope. The caller
 * passes a single shop's events.
 */

import {
  computeAdvisorMetrics,
  mean,
  percentile,
  type AdvisorActionEventLite,
  type CustodyEventLite,
} from '@/lib/tickets/lifecycle/advisorMetrics';
import type {
  AdvisorActionKind,
  CustodyEventKind,
  CustodyStatus,
} from '@/lib/tickets/lifecycle/types';
import {
  readCustodyHandoffMetadata,
  isPauseKickReason,
  isStopKickReason,
  isManualKickReason,
  getCustodyHandoffReasonLabel,
  IMPLICIT_TAKEOVER_REASON,
  type CustodyHandoffTrigger,
} from '@/lib/tickets/lifecycle/custodyHandoffReasons';
import {
  getPendingAcceptAgingLevel,
  CUSTODY_AGING_THRESHOLDS_MS,
} from '@/lib/tickets/lifecycle/custodyAging';

/** Default analytics window. Matches `ADVISOR_METRICS_WINDOW_DAYS` so
 *  the shop analytics and the Passport card cover the same period. */
export const SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS = 30;

// ─── Inputs ────────────────────────────────────────────────────────────

/**
 * A custody event row enriched with the Batch 1/2 reason + metadata so
 * trigger classification (`pause_kick` vs `stop_kick` vs `manual` vs
 * `implicit_takeover`) is possible. Superset of `CustodyEventLite`.
 */
export interface ShopCustodyEventInput {
  event_kind: CustodyEventKind;
  ticket_id: string;
  actor_id: string | null;
  created_at: string;
  /** `ticket_custody_events.reason` (free-form text). */
  reason: string | null;
  /** `ticket_custody_events.metadata` jsonb (carries `trigger`). */
  metadata: unknown;
}

export interface ShopAdvisorActionInput {
  action_kind: AdvisorActionKind;
  advisor_user_id: string | null;
  ticket_id: string;
  created_at: string;
}

export interface ShopCustodySnapshotRow {
  custody_status: CustodyStatus;
  holder_user_id: string | null;
  entered_at: string;
}

export interface ComputeShopAdvisorAnalyticsInput {
  /** All custody events in the window (kick_to_foh, foh_accept,
   *  kick_to_boh, boh_accept), shop-wide. */
  custodyEvents: ReadonlyArray<ShopCustodyEventInput>;
  /** Advisor action events in the window (ticket_accepted,
   *  kicked_back_to_tech, ticket_kicked_to_foh), shop-wide. */
  advisorActionEvents: ReadonlyArray<ShopAdvisorActionInput>;
  /** Current custody snapshot rows in the three FOH-attention states. */
  snapshotRows: ReadonlyArray<ShopCustodySnapshotRow>;
  /** userId → display name. Missing ids fall back to a short id. */
  advisorNames: Record<string, string>;
  /** Wall-clock reference for aging math (ISO). Injected for purity. */
  referenceTime: string;
}

// ─── Outputs ───────────────────────────────────────────────────────────

export interface ShopAdvisorOverview {
  currentFohQueueCount: number;
  pendingAdvisorAcceptCount: number;
  withAdvisorCount: number;
  pendingTechAcceptCount: number;
  averageAcceptLagMinutes: number | null;
  averageFohCustodyMinutes: number | null;
  /** foh_accept events (advisor took the ticket) in the window. */
  ticketsAcceptedCount: number;
  /** kick_to_boh events (advisor handed back to tech) in the window. */
  ticketsKickedBackToTechCount: number;
  /** kick_to_foh events (any kick to FOH) in the window. */
  ticketsKickedToFohCount: number;
  pauseKickCount: number;
  stopKickCount: number;
  manualKickCount: number;
}

export interface ShopTriggerBreakdown {
  pauseKickToFohCount: number;
  stopKickToFohCount: number;
  manualKickToFohCount: number;
  implicitTakeoverCount: number;
  /** kick_to_foh events whose trigger could not be classified
   *  (legacy rows with an ambiguous / missing reason + metadata). */
  unknownTriggerKickCount: number;
  averageAcceptLagMinutesPauseKick: number | null;
  averageAcceptLagMinutesStopKick: number | null;
  averageFohDwellMinutesPauseKick: number | null;
  averageFohDwellMinutesStopKick: number | null;
  /** Share (0..1) of pause-kicked tickets that were later kicked back
   *  to the tech. `null` when there is no pause-kicked ticket. */
  kickbackRatePauseKick: number | null;
  kickbackRateStopKick: number | null;
  sampleSize: {
    pauseKickAcceptLagPairs: number;
    stopKickAcceptLagPairs: number;
    pauseKickDwellPairs: number;
    stopKickDwellPairs: number;
    pauseKickTickets: number;
    stopKickTickets: number;
  };
}

export interface ShopReasonBreakdownEntry {
  /** Canonical reason value stored on the event. */
  value: string;
  /** Humanised label (via `getCustodyHandoffReasonLabel`). */
  label: string;
  count: number;
  /** Trigger group the reason was observed under. */
  trigger: CustodyHandoffTrigger | 'unknown';
}

export interface ShopAdvisorRow {
  advisorUserId: string;
  displayName: string;
  ticketsAcceptedCount: number;
  averageAcceptLagMinutes: number | null;
  p50AcceptLagMinutes: number | null;
  p95AcceptLagMinutes: number | null;
  averageFohCustodyMinutes: number | null;
  ticketsKickedBackToTechCount: number;
  /** advisor_action_events `ticket_kicked_to_foh`, attributable to
   *  this advisor (the person who performed the kick). */
  ticketKickedToFohCount: number;
  /** Current `with_advisor` rows held by this advisor right now. */
  currentWithAdvisorCount: number;
  /** foh_accept by this advisor where the originating kick was a
   *  pause-kick (tech-blocking handoff). */
  pauseKickHandledCount: number;
  /** …where the originating kick was a stop-kick (closeout handoff). */
  stopKickHandledCount: number;
  sampleSize: {
    acceptLagPairs: number;
    fohCustodyPairs: number;
  };
}

export interface ShopFohAgingView {
  pendingAdvisorAcceptCount: number;
  /** warning + urgent (≥ 5 min waiting for an advisor to accept). */
  agingCount: number;
  /** urgent only (≥ 10 min). */
  urgentCount: number;
  averageCurrentDwellMinutes: number | null;
  oldestTicketAgeMinutes: number | null;
}

export interface ShopAdvisorAnalytics {
  windowDays: number;
  overview: ShopAdvisorOverview;
  triggerBreakdown: ShopTriggerBreakdown;
  reasonBreakdown: ShopReasonBreakdownEntry[];
  perAdvisor: ShopAdvisorRow[];
  aging: ShopFohAgingView;
}

// ─── Trigger classification ──────────────────────────────────────────────

/**
 * Classify the handoff trigger for a kick event. The Batch 1/2
 * `metadata.trigger` is authoritative when present; otherwise we infer
 * from the (unambiguous) reason value so legacy rows still segment.
 * Returns `'unknown'` when neither is conclusive (e.g. a bare `other`
 * reason with no metadata, which belongs to multiple trigger groups).
 */
export function classifyKickTrigger(
  reason: string | null | undefined,
  metadata: unknown,
): CustodyHandoffTrigger | 'unknown' {
  const md = readCustodyHandoffMetadata(metadata);
  if (md?.trigger) return md.trigger;
  if (!reason) return 'unknown';
  if (reason === IMPLICIT_TAKEOVER_REASON) return 'implicit_takeover';
  const pause = isPauseKickReason(reason);
  const stop = isStopKickReason(reason);
  // `other` is shared across manual/pause/stop → ambiguous without
  // metadata. Only infer when the reason belongs to exactly one set.
  if (pause && !stop) return 'pause_kick';
  if (stop && !pause) return 'stop_kick';
  if (isManualKickReason(reason) && !pause && !stop) return 'manual';
  return 'unknown';
}

function parseMs(iso: string): number | null {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

// ─── Trigger-aware custody flow walk ─────────────────────────────────────

interface TriggeredPair {
  minutes: number;
  trigger: CustodyHandoffTrigger | 'unknown';
  advisorId: string | null;
}

interface CustodyFlowResult {
  /** kick_to_foh → foh_accept, tagged with the kick's trigger and the
   *  accepting advisor. */
  acceptLagPairs: TriggeredPair[];
  /** foh_accept → kick_to_boh, tagged with the originating kick's
   *  trigger and the holding advisor. */
  dwellPairs: TriggeredPair[];
  /** First kick_to_foh trigger observed per ticket. */
  perTicketTrigger: Map<string, CustodyHandoffTrigger | 'unknown'>;
  /** Tickets that had at least one kick_to_boh after a foh_accept. */
  perTicketKickedBack: Set<string>;
}

/**
 * Single ascending walk per ticket that pairs custody transitions and
 * carries the originating kick's trigger forward, so the same pass
 * yields accept-lag-by-trigger, FOH-dwell-by-trigger, and per-ticket
 * kickback flags. Out-of-order / unparseable timestamps are dropped
 * (clock-skew defence, mirroring `advisorMetrics`).
 */
function walkCustodyFlows(
  events: ReadonlyArray<ShopCustodyEventInput>,
): CustodyFlowResult {
  const byTicket = new Map<string, ShopCustodyEventInput[]>();
  for (const ev of events) {
    if (!byTicket.has(ev.ticket_id)) byTicket.set(ev.ticket_id, []);
    byTicket.get(ev.ticket_id)!.push(ev);
  }

  const acceptLagPairs: TriggeredPair[] = [];
  const dwellPairs: TriggeredPair[] = [];
  const perTicketTrigger = new Map<string, CustodyHandoffTrigger | 'unknown'>();
  const perTicketKickedBack = new Set<string>();

  for (const [ticketId, list] of byTicket) {
    const sorted = [...list].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    );
    // Pending kick_to_foh awaiting a foh_accept.
    let pendingKickMs: number | null = null;
    let pendingKickTrigger: CustodyHandoffTrigger | 'unknown' = 'unknown';
    // Active FOH custody (after a foh_accept) awaiting kick_to_boh.
    let activeFohMs: number | null = null;
    let activeFohAdvisor: string | null = null;
    let activeFohTrigger: CustodyHandoffTrigger | 'unknown' = 'unknown';

    for (const ev of sorted) {
      if (ev.event_kind === 'kick_to_foh') {
        pendingKickMs = parseMs(ev.created_at);
        pendingKickTrigger = classifyKickTrigger(ev.reason, ev.metadata);
        if (!perTicketTrigger.has(ticketId)) {
          perTicketTrigger.set(ticketId, pendingKickTrigger);
        }
        continue;
      }
      if (ev.event_kind === 'foh_accept') {
        const acceptMs = parseMs(ev.created_at);
        if (acceptMs != null && pendingKickMs != null) {
          const elapsed = (acceptMs - pendingKickMs) / 60_000;
          if (elapsed >= 0) {
            acceptLagPairs.push({
              minutes: elapsed,
              trigger: pendingKickTrigger,
              advisorId: ev.actor_id,
            });
          }
        }
        // Open the FOH-dwell window regardless of accept-lag validity.
        activeFohMs = acceptMs;
        activeFohAdvisor = ev.actor_id;
        activeFohTrigger = pendingKickTrigger;
        pendingKickMs = null;
        continue;
      }
      if (ev.event_kind === 'kick_to_boh') {
        perTicketKickedBack.add(ticketId);
        if (activeFohMs != null) {
          const backMs = parseMs(ev.created_at);
          if (backMs != null) {
            const elapsed = (backMs - activeFohMs) / 60_000;
            if (elapsed >= 0) {
              dwellPairs.push({
                minutes: elapsed,
                trigger: activeFohTrigger,
                advisorId: activeFohAdvisor,
              });
            }
          }
        }
        activeFohMs = null;
        activeFohAdvisor = null;
        continue;
      }
      if (ev.event_kind === 'boh_accept') {
        // Tech took it back; reset cycle state for any later re-kick.
        activeFohMs = null;
        activeFohAdvisor = null;
        pendingKickMs = null;
      }
    }
  }

  return { acceptLagPairs, dwellPairs, perTicketTrigger, perTicketKickedBack };
}

// ─── Main ────────────────────────────────────────────────────────────────

export function computeShopAdvisorAnalytics(
  input: ComputeShopAdvisorAnalyticsInput,
): ShopAdvisorAnalytics {
  const { custodyEvents, advisorActionEvents, snapshotRows, advisorNames } =
    input;

  // ── Snapshot counts (current FOH queue) ──
  let pendingAdvisorAcceptCount = 0;
  let withAdvisorCount = 0;
  let pendingTechAcceptCount = 0;
  const withAdvisorByHolder = new Map<string, number>();
  const pendingDwellMinutes: number[] = [];
  let agingCount = 0;
  let urgentCount = 0;
  let oldestTicketAgeMinutes: number | null = null;
  const refMs = parseMs(input.referenceTime);

  for (const row of snapshotRows) {
    switch (row.custody_status) {
      case 'pending_advisor_accept': {
        pendingAdvisorAcceptCount += 1;
        const level = getPendingAcceptAgingLevel(
          row.entered_at,
          input.referenceTime,
        );
        if (level === 'urgent') {
          urgentCount += 1;
          agingCount += 1;
        } else if (level === 'warning') {
          agingCount += 1;
        }
        const enteredMs = parseMs(row.entered_at);
        if (enteredMs != null && refMs != null) {
          const ageMin = (refMs - enteredMs) / 60_000;
          if (ageMin >= 0) {
            pendingDwellMinutes.push(ageMin);
            if (oldestTicketAgeMinutes == null || ageMin > oldestTicketAgeMinutes) {
              oldestTicketAgeMinutes = ageMin;
            }
          }
        }
        break;
      }
      case 'with_advisor': {
        withAdvisorCount += 1;
        if (row.holder_user_id) {
          withAdvisorByHolder.set(
            row.holder_user_id,
            (withAdvisorByHolder.get(row.holder_user_id) ?? 0) + 1,
          );
        }
        break;
      }
      case 'pending_tech_accept':
        pendingTechAcceptCount += 1;
        break;
      default:
        break;
    }
  }

  // ── Event tallies (shop overview headline counts) ──
  let ticketsAcceptedCount = 0;
  let ticketsKickedBackToTechCount = 0;
  let ticketsKickedToFohCount = 0;
  let pauseKickCount = 0;
  let stopKickCount = 0;
  let manualKickCount = 0;
  let implicitTakeoverCount = 0;
  let unknownTriggerKickCount = 0;

  // Reason breakdown keyed by `${trigger}|${value}` so a reason that
  // appears under multiple triggers (e.g. `other`) stays separable.
  const reasonMap = new Map<
    string,
    { value: string; trigger: CustodyHandoffTrigger | 'unknown'; count: number }
  >();

  for (const ev of custodyEvents) {
    switch (ev.event_kind) {
      case 'foh_accept':
        ticketsAcceptedCount += 1;
        break;
      case 'kick_to_boh':
        ticketsKickedBackToTechCount += 1;
        break;
      case 'kick_to_foh': {
        ticketsKickedToFohCount += 1;
        const trigger = classifyKickTrigger(ev.reason, ev.metadata);
        switch (trigger) {
          case 'pause_kick':
            pauseKickCount += 1;
            break;
          case 'stop_kick':
            stopKickCount += 1;
            break;
          case 'manual':
            manualKickCount += 1;
            break;
          case 'implicit_takeover':
            implicitTakeoverCount += 1;
            break;
          default:
            unknownTriggerKickCount += 1;
            break;
        }
        // Reason tally (prefer the metadata snapshot value).
        const md = readCustodyHandoffMetadata(ev.metadata);
        const value = md?.trigger_reason ?? ev.reason ?? 'unspecified';
        const key = `${trigger}|${value}`;
        const existing = reasonMap.get(key);
        if (existing) existing.count += 1;
        else reasonMap.set(key, { value, trigger, count: 1 });
        break;
      }
      default:
        break;
    }
  }

  const reasonBreakdown: ShopReasonBreakdownEntry[] = [...reasonMap.values()]
    .map((r) => ({
      value: r.value,
      label: getCustodyHandoffReasonLabel(r.value),
      count: r.count,
      trigger: r.trigger,
    }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));

  // ── Trigger-aware flow walk (shop + pause/stop segmentation) ──
  const flows = walkCustodyFlows(custodyEvents);

  const allAcceptLags = flows.acceptLagPairs.map((p) => p.minutes);
  const allDwells = flows.dwellPairs.map((p) => p.minutes);

  const pauseAcceptLags = flows.acceptLagPairs
    .filter((p) => p.trigger === 'pause_kick')
    .map((p) => p.minutes);
  const stopAcceptLags = flows.acceptLagPairs
    .filter((p) => p.trigger === 'stop_kick')
    .map((p) => p.minutes);
  const pauseDwells = flows.dwellPairs
    .filter((p) => p.trigger === 'pause_kick')
    .map((p) => p.minutes);
  const stopDwells = flows.dwellPairs
    .filter((p) => p.trigger === 'stop_kick')
    .map((p) => p.minutes);

  // Per-trigger kickback rate (per-ticket, by first kick trigger).
  let pauseKickTickets = 0;
  let pauseKickKickedBack = 0;
  let stopKickTickets = 0;
  let stopKickKickedBack = 0;
  for (const [ticketId, trigger] of flows.perTicketTrigger) {
    const kickedBack = flows.perTicketKickedBack.has(ticketId);
    if (trigger === 'pause_kick') {
      pauseKickTickets += 1;
      if (kickedBack) pauseKickKickedBack += 1;
    } else if (trigger === 'stop_kick') {
      stopKickTickets += 1;
      if (kickedBack) stopKickKickedBack += 1;
    }
  }

  const triggerBreakdown: ShopTriggerBreakdown = {
    pauseKickToFohCount: pauseKickCount,
    stopKickToFohCount: stopKickCount,
    manualKickToFohCount: manualKickCount,
    implicitTakeoverCount,
    unknownTriggerKickCount,
    averageAcceptLagMinutesPauseKick: mean(pauseAcceptLags),
    averageAcceptLagMinutesStopKick: mean(stopAcceptLags),
    averageFohDwellMinutesPauseKick: mean(pauseDwells),
    averageFohDwellMinutesStopKick: mean(stopDwells),
    kickbackRatePauseKick:
      pauseKickTickets > 0 ? pauseKickKickedBack / pauseKickTickets : null,
    kickbackRateStopKick:
      stopKickTickets > 0 ? stopKickKickedBack / stopKickTickets : null,
    sampleSize: {
      pauseKickAcceptLagPairs: pauseAcceptLags.length,
      stopKickAcceptLagPairs: stopAcceptLags.length,
      pauseKickDwellPairs: pauseDwells.length,
      stopKickDwellPairs: stopDwells.length,
      pauseKickTickets,
      stopKickTickets,
    },
  };

  // ── Per-advisor rows ──
  // Distinct advisors = union of (foh_accept actors) ∪ (with_advisor
  // holders) ∪ (advisor_action advisor_user_ids).
  const advisorIds = new Set<string>();
  for (const ev of custodyEvents) {
    if (ev.event_kind === 'foh_accept' && ev.actor_id) {
      advisorIds.add(ev.actor_id);
    }
  }
  for (const id of withAdvisorByHolder.keys()) advisorIds.add(id);
  for (const ev of advisorActionEvents) {
    if (ev.advisor_user_id) advisorIds.add(ev.advisor_user_id);
  }

  // Map custody events down to the lite shape `computeAdvisorMetrics`
  // expects (drops reason/metadata it does not read).
  const liteCustody: CustodyEventLite[] = custodyEvents.map((ev) => ({
    event_kind: ev.event_kind,
    ticket_id: ev.ticket_id,
    actor_id: ev.actor_id,
    created_at: ev.created_at,
  }));

  // Pre-bucket pause/stop-handled counts by accepting advisor.
  const pauseHandledByAdvisor = new Map<string, number>();
  const stopHandledByAdvisor = new Map<string, number>();
  for (const p of flows.acceptLagPairs) {
    if (!p.advisorId) continue;
    if (p.trigger === 'pause_kick') {
      pauseHandledByAdvisor.set(
        p.advisorId,
        (pauseHandledByAdvisor.get(p.advisorId) ?? 0) + 1,
      );
    } else if (p.trigger === 'stop_kick') {
      stopHandledByAdvisor.set(
        p.advisorId,
        (stopHandledByAdvisor.get(p.advisorId) ?? 0) + 1,
      );
    }
  }

  // ticket_kicked_to_foh attributable to each advisor.
  const kickedToFohByAdvisor = new Map<string, number>();
  for (const ev of advisorActionEvents) {
    if (ev.action_kind === 'ticket_kicked_to_foh' && ev.advisor_user_id) {
      kickedToFohByAdvisor.set(
        ev.advisor_user_id,
        (kickedToFohByAdvisor.get(ev.advisor_user_id) ?? 0) + 1,
      );
    }
  }

  const perAdvisor: ShopAdvisorRow[] = [];
  for (const advisorUserId of advisorIds) {
    // Reuse the Passport source of truth: counts come from this
    // advisor's action events, timing from the shop-wide custody events
    // (the helper filters by actor internally).
    const advisorActions: AdvisorActionEventLite[] = advisorActionEvents
      .filter((ev) => ev.advisor_user_id === advisorUserId)
      .map((ev) => ({
        action_kind: ev.action_kind,
        ticket_id: ev.ticket_id,
        created_at: ev.created_at,
      }));
    const metrics = computeAdvisorMetrics({
      advisorActionEvents: advisorActions,
      custodyEvents: liteCustody,
      advisorUserId,
      currentCounts: {
        pendingAdvisorAccept: 0,
        withAdvisor: withAdvisorByHolder.get(advisorUserId) ?? 0,
      },
    });

    perAdvisor.push({
      advisorUserId,
      displayName: advisorNames[advisorUserId] ?? shortId(advisorUserId),
      ticketsAcceptedCount: metrics.ticketsAcceptedCount,
      averageAcceptLagMinutes: metrics.averageAcceptLagMinutes,
      p50AcceptLagMinutes: metrics.p50AcceptLagMinutes,
      p95AcceptLagMinutes: metrics.p95AcceptLagMinutes,
      averageFohCustodyMinutes: metrics.averageFohCustodyMinutes,
      ticketsKickedBackToTechCount: metrics.ticketsKickedBackToTechCount,
      ticketKickedToFohCount: kickedToFohByAdvisor.get(advisorUserId) ?? 0,
      currentWithAdvisorCount: withAdvisorByHolder.get(advisorUserId) ?? 0,
      pauseKickHandledCount: pauseHandledByAdvisor.get(advisorUserId) ?? 0,
      stopKickHandledCount: stopHandledByAdvisor.get(advisorUserId) ?? 0,
      sampleSize: {
        acceptLagPairs: metrics.sampleSize.acceptLagPairs,
        fohCustodyPairs: metrics.sampleSize.fohCustodyPairs,
      },
    });
  }

  // Stable order: most tickets accepted first, then name.
  perAdvisor.sort(
    (a, b) =>
      b.ticketsAcceptedCount - a.ticketsAcceptedCount ||
      a.displayName.localeCompare(b.displayName),
  );

  const overview: ShopAdvisorOverview = {
    currentFohQueueCount:
      pendingAdvisorAcceptCount + withAdvisorCount + pendingTechAcceptCount,
    pendingAdvisorAcceptCount,
    withAdvisorCount,
    pendingTechAcceptCount,
    averageAcceptLagMinutes: mean(allAcceptLags),
    averageFohCustodyMinutes: mean(allDwells),
    ticketsAcceptedCount,
    ticketsKickedBackToTechCount,
    ticketsKickedToFohCount,
    pauseKickCount,
    stopKickCount,
    manualKickCount,
  };

  const aging: ShopFohAgingView = {
    pendingAdvisorAcceptCount,
    agingCount,
    urgentCount,
    averageCurrentDwellMinutes: mean(pendingDwellMinutes),
    oldestTicketAgeMinutes,
  };

  return {
    windowDays: SHOP_ADVISOR_ANALYTICS_WINDOW_DAYS,
    overview,
    triggerBreakdown,
    reasonBreakdown,
    perAdvisor,
    aging,
  };
}

/** Re-exported for the FOH aging UI legend. */
export { CUSTODY_AGING_THRESHOLDS_MS };
